import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useListboxCombobox } from '../../lib/hooks/useListboxCombobox';
import { useNavigate } from 'react-router-dom';
import { LoadingIndicator, LoadingSpinner } from '../../components/onboarding/LoadingIndicator';
import {
  Briefcase,
  Check,
  ChevronDown,
  Lock,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import OnboardingStepper, {
  OnboardingStep,
} from '../../components/onboarding/OnboardingStepper';
import OnboardingSubHeader from '../../components/onboarding/OnboardingSubHeader';
import {
  OnboardingNestedPanel,
  OnboardingSurface,
} from '../../components/onboarding/Surface';
import { useAuth } from '../../contexts/AuthContext';
import {
  DEFAULT_LIST_PAGE_SIZE,
  getUserByEmail,
  listProjects,
} from '../../services/onboardingApi';
import type { BackendProjectDTO } from '../../services/onboardingTypes';
import { projectFromBackend, userFromBackend } from '../../services/onboardingTransforms';
import { usePaginatedList } from '../../lib/hooks/usePaginatedList';
import { useScrollPaginatedLoadMore } from '../../lib/hooks/useScrollPaginatedLoadMore';
import { canCreateUsers, formatPlatformRoleLabel, isPlatformAdminUser, MAX_ORG_PROJECT_NAME_LENGTH, ORG_PROJECT_NAME_LENGTH_HINT, Organization, PlatformRole, Project, User, isOrgOrProjectNameTooLong, orgOrProjectNameLengthMessage, validatePersonName, validatePhoneNumber } from '../../types/onboarding';
import { Button } from '../../components/onboarding/Button';
import { LockedField } from '../../components/onboarding/LockedField';
import { Dialog } from '../../components/onboarding/Dialog';
import { Input } from '../../components/onboarding/Input';
import { Label } from '../../components/onboarding/Label';
import { twMerge as cn } from 'tailwind-merge';
import { pageRoutes } from '../../constants/routes';
import {
  ActiveOrganizationNotice,
  PlatformAdminOrgNotice,
} from '../../components/onboarding/ActiveOrganizationNotice';
import { RoleMenuOption } from '../../components/onboarding/RoleMenuOption';
import { PLATFORM_ROLE_OPTIONS } from '../../constants/platformRoleOptions';

const USER_STEPS: OnboardingStep[] = [
  {
    id: 1,
    title: 'User Details',
    description: 'Enter basic user information and contact details.',
  },
  {
    id: 2,
    title: 'Role & Organization',
    description: 'Assign role and organization membership.',
  },
  {
    id: 3,
    title: 'Project Assignments',
    description: 'Assign the user to projects within their organization.',
  },
  {
    id: 4,
    title: 'Review & Create',
    description: 'Review user details and confirm creation.',
  },
];

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  organization: string;
  platformRole: PlatformRole | '';
}

type UserFormErrors = Partial<Record<keyof UserFormData, string>>;

function resolveOrganizationMatch(
  organizations: Organization[],
  orgName: string
): { id?: number; name: string } | null {
  const trimmed = orgName.trim();
  if (!trimmed) return null;
  const key = trimmed.toLowerCase();
  const org = organizations.find((o) => o.name.trim().toLowerCase() === key);
  if (!org) return { name: trimmed };
  const id = Number(org.id);
  return {
    id: Number.isFinite(id) ? id : undefined,
    name: org.name,
  };
}

function userBelongsToOrganization(
  user: User,
  orgName: string,
  organizations: Organization[]
): boolean {
  const match = resolveOrganizationMatch(organizations, orgName);
  if (!match) return false;

  if (match.id != null) {
    if (user.activeOrganizationId === match.id) return true;
    if ((user.memberships ?? []).some((m) => m.organizationId === match.id)) {
      return true;
    }
  }

  const key = match.name.toLowerCase();
  if (user.organization.trim().toLowerCase() === key) return true;
  return (user.memberships ?? []).some(
    (membership) =>
      membership.organizationName.trim().toLowerCase() === key
  );
}

function isPlatformAdminAccount(user: User): boolean {
  return Boolean(user.isPlatformAdmin || user.platformRole === 'admin');
}

const ADMIN_ACCOUNT_EMAIL_ERROR =
  'This email belongs to an Admin account and cannot be invited through this form.';

function isAdminOrgInviteError(message: string): boolean {
  const lower = String(message).toLowerCase();
  return (
    lower.includes('admins cannot be added to organizations') ||
    (lower.includes('platform admin') && lower.includes('organization'))
  );
}

type ExistingUserAssignmentErrors = Pick<
  UserFormErrors,
  'email' | 'organization'
>;

function validateExistingUserAssignment(
  existing: User,
  options: {
    organizations: Organization[];
    organizationName?: string;
    orgRequired: boolean;
    creatingPlatformAdmin: boolean;
    isPlatformAdminViewer: boolean;
  }
): ExistingUserAssignmentErrors {
  const errors: ExistingUserAssignmentErrors = {};
  const { organizationName, orgRequired, creatingPlatformAdmin } = options;

  if (options.isPlatformAdminViewer && creatingPlatformAdmin) {
    errors.email =
      'This account already exists. Use Edit User to grant Admin access.';
    return errors;
  }

  if (
    orgRequired &&
    organizationName &&
    userBelongsToOrganization(existing, organizationName, options.organizations)
  ) {
    errors.organization =
      'This user already belongs to the selected organization. Use Edit User to change their role or contact details.';
  }

  return errors;
}

function UserOnboarding() {
  const navigate = useNavigate();
  const {
    currentUser,
    memberships,
    organizations,
    addUser,
    addOrgUser,
    addOrganizationName,
    updateProject,
    ensureOrganizations,
    isLoading,
  } = useAuth();

  useEffect(() => {
    void ensureOrganizations();
  }, [ensureOrganizations]);
  const isPlatformAdmin = isPlatformAdminUser(currentUser);
  const creatableOrganizations = useMemo(() => {
    if (isPlatformAdmin) return organizations;
    const maintainerOrgIds = new Set(
      memberships
        .filter((membership) => membership.orgRole === 'maintainer')
        .map((membership) => membership.organizationId)
    );
    return organizations.filter((org) => {
      const numericId = Number(org.id);
      if (!Number.isNaN(numericId) && maintainerOrgIds.has(numericId)) {
        return true;
      }
      return memberships.some(
        (membership) =>
          membership.orgRole === 'maintainer' &&
          membership.organizationName.toLowerCase() === org.name.toLowerCase()
      );
    });
  }, [isPlatformAdmin, organizations, memberships]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const orgProjectsScrollRef = useRef<HTMLDivElement>(null);
  const orgProjectsSentinelRef = useRef<HTMLDivElement>(null);
  const [roleMenuRect, setRoleMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const [orgMenuRect, setOrgMenuRect] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const rolePortalMenuRef = useRef<HTMLDivElement>(null);
  const orgPortalMenuRef = useRef<HTMLDivElement>(null);
  const orgSearchInputRef = useRef<HTMLInputElement>(null);
  const newOrgInputRef = useRef<HTMLInputElement>(null);
  const emailLookupCacheRef = useRef<{
    emailKey: string;
    user: User | null;
  } | null>(null);

  const invalidateEmailLookup = useCallback(() => {
    emailLookupCacheRef.current = null;
  }, []);

  const lookupExistingUser = useCallback(async (email: string): Promise<User | null> => {
    const trimmed = email.trim();
    const key = trimmed.toLowerCase();
    if (!key) return null;

    if (emailLookupCacheRef.current?.emailKey === key) {
      return emailLookupCacheRef.current.user;
    }

    const dto = await getUserByEmail(trimmed);
    const user = dto ? userFromBackend(dto) : null;
    emailLookupCacheRef.current = { emailKey: key, user };
    return user;
  }, []);

  const closeAddOrgModal = useCallback(() => {
    setShowAddOrgModal(false);
    setNewOrgName('');
  }, []);

  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    organization: '',
    platformRole: '',
  });
  const [errors, setErrors] = useState<UserFormErrors>({});

  const clearFieldErrors = useCallback((...fields: (keyof UserFormErrors)[]) => {
    setErrors((prev) => {
      const toClear = fields.filter((field) => prev[field]);
      if (toClear.length === 0) return prev;
      const next = { ...prev };
      for (const field of toClear) {
        delete next[field];
      }
      return next;
    });
    setSubmitError(null);
  }, []);

  const filteredOrganizations = creatableOrganizations.filter((org) =>
    org.name.toLowerCase().includes(orgSearchQuery.toLowerCase()),
  );

  // Project assignment is scoped to the user's organisation: the backend
  // `add_project_user` lambda requires `organisation_name` to match the
  // project's customer, so showing cross-org projects would only produce
  // submit-time 400s.
  const selectedOrgName = formData.organization;
  const selectedOrg = useMemo(
    () =>
      organizations.find(
        (entry) =>
          entry.name.trim().toLowerCase() === selectedOrgName.trim().toLowerCase()
      ),
    [organizations, selectedOrgName]
  );
  const selectedOrgId = useMemo(() => {
    const orgId = selectedOrg ? Number(selectedOrg.id) : undefined;
    return orgId != null && !Number.isNaN(orgId) && orgId > 0 ? orgId : undefined;
  }, [selectedOrg]);

  const fetchOrgProjectsPage = useCallback(
    async ({
      pageNumber,
      pageSize,
      signal,
      organizationId,
    }: {
      pageNumber: number;
      pageSize: number;
      signal?: AbortSignal;
      organizationId?: number;
    }) => {
      const orgId =
        typeof organizationId === 'number' ? organizationId : undefined;
      if (orgId == null || orgId <= 0) {
        return {
          items: [],
          page_number: 0,
          page_size: pageSize,
          total_count: 0,
          has_more: false,
        };
      }
      const response = await listProjects({
        pageNumber,
        pageSize,
        organizationId: orgId,
        signal,
      });
      return {
        items: response.projects ?? [],
        page_number: response.page_number,
        page_size: response.page_size,
        total_count: response.total_count,
        has_more: response.has_more,
      };
    },
    []
  );

  const {
    items: orgProjectDtos,
    isLoading: orgProjectsLoading,
    isLoadingMore: orgProjectsLoadingMore,
    hasMore: orgProjectsHasMore,
    totalCount: orgProjectsTotalCount,
    loadMore: loadMoreOrgProjects,
  } = usePaginatedList<BackendProjectDTO>({
    pageSize: DEFAULT_LIST_PAGE_SIZE,
    fetchPage: fetchOrgProjectsPage,
    filters: { organizationId: selectedOrgId },
    enabled: Boolean(selectedOrgName.trim() && selectedOrgId),
    getItemKey: (item) => item.project_id,
  });

  const orgProjects = useMemo((): Project[] => {
    if (!selectedOrgName.trim()) return [];
    const orgKey = selectedOrgName.trim().toLowerCase();
    return orgProjectDtos
      .filter(
        (dto) => dto.organization_name.trim().toLowerCase() === orgKey
      )
      .map((dto) => projectFromBackend(dto));
  }, [orgProjectDtos, selectedOrgName]);

  const assignableProjects = orgProjects;

  useScrollPaginatedLoadMore({
    scrollContainerRef: orgProjectsScrollRef,
    sentinelRef: orgProjectsSentinelRef,
    hasMore: orgProjectsHasMore,
    isLoading: orgProjectsLoading,
    isLoadingMore: orgProjectsLoadingMore,
    onLoadMore: loadMoreOrgProjects,
    enabled: Boolean(selectedOrgName.trim() && currentStep === 3),
    itemCount: orgProjects.length,
  });

  useEffect(() => {
    if (!selectedOrgName) {
      setSelectedProjects([]);
    }
  }, [selectedOrgName]);

  const toggleProjectAssignment = useCallback((projectId: string) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  }, []);

  // Clear stale picks if the admin walks back to Step 2 and changes the org —
  // those projects no longer belong to the user's new organisation.
  useEffect(() => {
    setSelectedProjects([]);
  }, [formData.organization]);

  useEffect(() => {
    clearFieldErrors('organization');
  }, [formData.organization, clearFieldErrors]);

  const selectOrganization = useCallback(
    (index: number) => {
      const org = filteredOrganizations[index];
      if (!org) return;
      clearFieldErrors('organization');
      setFormData((prev) => ({ ...prev, organization: org.name }));
      setOrgSearchQuery('');
    },
    [filteredOrganizations, clearFieldErrors],
  );

  const orgMenu = useListboxCombobox({
    itemCount: filteredOrganizations.length,
    onSelect: selectOrganization,
    additionalRefs: [orgPortalMenuRef],
  });

  const canCreate = Boolean(
    currentUser && canCreateUsers(currentUser.platformRole)
  );
  const availableRoles = useMemo(
    () =>
      isPlatformAdmin
        ? PLATFORM_ROLE_OPTIONS
        : PLATFORM_ROLE_OPTIONS.filter((role) => role.value !== 'admin'),
    [isPlatformAdmin]
  );
  const creatingPlatformAdmin =
    isPlatformAdmin && formData.platformRole === 'admin';
  const creatingMaintainer = formData.platformRole === 'maintainer';
  const creatingDeveloper = formData.platformRole === 'developer';
  const orgRequiredForRole = !creatingPlatformAdmin;

  const selectPlatformRole = useCallback((index: number) => {
    const role = availableRoles[index];
    if (!role) return;
    clearFieldErrors('platformRole', 'email', 'organization');
    setFormData((prev) => ({
      ...prev,
      platformRole: role.value,
      ...(role.value === 'admin' ? { organization: '' } : {}),
    }));
    if (role.value === 'admin' || role.value === 'maintainer') {
      setSelectedProjects([]);
    }
  }, [availableRoles, clearFieldErrors]);

  const roleAdditionalRefs = useMemo(() => [rolePortalMenuRef], []);
  const roleMenu = useListboxCombobox({
    itemCount: availableRoles.length,
    onSelect: selectPlatformRole,
    additionalRefs: roleAdditionalRefs,
  });

  const measureRoleMenuRect = useCallback(() => {
    const el = roleMenu.containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const reservedBottom = 16;
    const spaceBelow = window.innerHeight - rect.bottom - gap - reservedBottom;
    const maxHeight = Math.max(120, Math.min(360, spaceBelow));
    setRoleMenuRect({
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  }, [roleMenu.containerRef]);

  const measureOrgMenuRect = useCallback(() => {
    const el = orgMenu.containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const reservedBottom = 16;
    const spaceBelow = window.innerHeight - rect.bottom - gap - reservedBottom;
    const maxHeight = Math.max(160, Math.min(320, spaceBelow));
    setOrgMenuRect({
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  }, [orgMenu.containerRef]);

  useLayoutEffect(() => {
    if (!roleMenu.isOpen) {
      setRoleMenuRect(null);
      return;
    }
    measureRoleMenuRect();
    window.addEventListener('resize', measureRoleMenuRect);
    window.addEventListener('scroll', measureRoleMenuRect, true);
    return () => {
      window.removeEventListener('resize', measureRoleMenuRect);
      window.removeEventListener('scroll', measureRoleMenuRect, true);
    };
  }, [roleMenu.isOpen, measureRoleMenuRect]);

  useLayoutEffect(() => {
    if (!orgMenu.isOpen) {
      setOrgMenuRect(null);
      return;
    }
    measureOrgMenuRect();
    window.addEventListener('resize', measureOrgMenuRect);
    window.addEventListener('scroll', measureOrgMenuRect, true);
    return () => {
      window.removeEventListener('resize', measureOrgMenuRect);
      window.removeEventListener('scroll', measureOrgMenuRect, true);
    };
  }, [orgMenu.isOpen, measureOrgMenuRect]);

  // When the search-enabled org dropdown opens, move focus to the search input
  // so typing and arrow-key navigation "just work" without a second click.
  useEffect(() => {
    if (orgMenu.isOpen) {
      orgSearchInputRef.current?.focus();
    }
  }, [orgMenu.isOpen]);

  useEffect(() => {
    if (isPlatformAdmin || !currentUser?.organization || formData.organization) {
      return;
    }
    const activeOrg = organizations.find(
      (org) =>
        org.name.toLowerCase() === currentUser.organization.toLowerCase()
    );
    setFormData((prev) => ({
      ...prev,
      organization: activeOrg?.name ?? currentUser.organization ?? prev.organization,
    }));
  }, [isPlatformAdmin, currentUser?.organization, formData.organization, organizations]);

  if (isLoading && !currentUser) {
    return (
      <>
        <OnboardingSubHeader title="User Creation" showBackButton />
        <main className="flex flex-1 flex-col bg-surface-muted">
          <LoadingIndicator layout="page" />
        </main>
      </>
    );
  }

  const validateStep1 = async (): Promise<boolean> => {
    const newErrors: UserFormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else {
      try {
        const existing = await lookupExistingUser(formData.email);
        if (existing) {
          if (isPlatformAdminAccount(existing)) {
            newErrors.email = ADMIN_ACCOUNT_EMAIL_ERROR;
          } else if (!isPlatformAdmin) {
            const targetOrg =
              formData.organization.trim() ||
              currentUser?.organization?.trim() ||
              '';
            if (
              targetOrg &&
              userBelongsToOrganization(existing, targetOrg, organizations)
            ) {
              newErrors.email =
                'A user with this email already belongs to this organization. Use Edit User to update their details.';
            }
          }
        }
      } catch {
        newErrors.email = 'Unable to verify this email address. Please try again.';
      }
    }

    if (canCreate) {
      const firstNameError = validatePersonName(formData.firstName);
      if (firstNameError) {
        newErrors.firstName = firstNameError;
      }

      const lastNameError = validatePersonName(formData.lastName);
      if (lastNameError) {
        newErrors.lastName = lastNameError;
      }

      const phoneError = validatePhoneNumber(formData.phone);
      if (phoneError) {
        newErrors.phone = phoneError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildStep2Errors = async (): Promise<UserFormErrors> => {
    const newErrors: UserFormErrors = {};
    if (orgRequiredForRole && !formData.organization) {
      newErrors.organization = 'Organization is required';
    } else if (
      formData.organization &&
      isOrgOrProjectNameTooLong(formData.organization)
    ) {
      newErrors.organization = orgOrProjectNameLengthMessage('Organization name');
    }
    if (!formData.platformRole) {
      newErrors.platformRole = 'Role is required';
    }

    try {
      const existing = await lookupExistingUser(formData.email);
      if (existing) {
        const assignmentErrors = validateExistingUserAssignment(existing, {
          organizations,
          organizationName: formData.organization,
          orgRequired: orgRequiredForRole,
          creatingPlatformAdmin,
          isPlatformAdminViewer: isPlatformAdmin,
        });
        Object.assign(newErrors, assignmentErrors);
      }
    } catch {
      newErrors.email = 'Unable to verify this email address. Please try again.';
    }

    return newErrors;
  };

  const applyStep2Validation = async (): Promise<UserFormErrors> => {
    const step2Errors = await buildStep2Errors();
    setErrors(step2Errors);
    if (step2Errors.email) {
      setCurrentStep(1);
    } else if (step2Errors.organization || step2Errors.platformRole) {
      setCurrentStep(2);
    }
    return step2Errors;
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!(await validateStep1())) return;
    }
    if (currentStep === 2) {
      const step2Errors = await applyStep2Validation();
      if (Object.keys(step2Errors).length > 0) {
        return;
      }
    }
    setSubmitError(null);
    setCurrentStep((prev) => Math.min(prev + 1, USER_STEPS.length));
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!formData.platformRole || submitting) return;
    if (!(await validateStep1())) {
      setCurrentStep(1);
      return;
    }
    const step2Errors = await buildStep2Errors();
    if (Object.keys(step2Errors).length > 0) {
      await applyStep2Validation();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const organizationName = formData.organization.trim() || undefined;
      const existingUser = await lookupExistingUser(formData.email);
      const addExistingUserToOrg =
        isPlatformAdmin &&
        existingUser != null &&
        !creatingPlatformAdmin &&
        !isPlatformAdminAccount(existingUser) &&
        Boolean(organizationName);

      const created = addExistingUserToOrg || !isPlatformAdmin
        ? await addOrgUser({
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone || undefined,
            organization: organizationName ?? '',
            orgRole: formData.platformRole as PlatformRole,
          })
        : await addUser({
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone || undefined,
            ...(organizationName ? { organization: organizationName } : {}),
            platformRole: formData.platformRole as PlatformRole,
          });

      // The user is already created at this point — partial failures here
      // shouldn't roll back the user. Accumulate per-project errors and
      // surface them after the loop so the admin can retry assignments
      // from Edit User without re-creating the user record.
      const failedProjectNames: string[] = [];
      if (creatingDeveloper) {
        for (const projectId of selectedProjects) {
          try {
            await updateProject(projectId, { addUserEmail: created.email });
          } catch (assignErr) {
            const project = orgProjects.find((p) => p.id === projectId);
            failedProjectNames.push(project?.name ?? `project ${projectId}`);
            // eslint-disable-next-line no-console
            console.error(
              `Failed to assign user ${created.email} to project ${projectId}`,
              assignErr,
            );
          }
        }
      }

      if (failedProjectNames.length > 0) {
        setSubmitError(
          `User created, but failed to assign to: ${failedProjectNames.join(
            ', ',
          )}. You can retry from Edit User.`,
        );
        setSubmitting(false);
        return;
      }

      navigate(`${pageRoutes.onboarding}?success=user`);
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        err?.displayMessage ?? 'Failed to create user. Please try again.';
      const lower = String(message).toLowerCase();
      if (lower.includes('phone')) {
        setErrors({ phone: message });
        setCurrentStep(1);
        setSubmitting(false);
        return;
      }
      if (
        status === 409 ||
        (lower.includes('already') && lower.includes('organization'))
      ) {
        const orgConflictMessage =
          'This user already belongs to the selected organization. Use Edit User to change role or contact details.';
        if (isPlatformAdmin) {
          setErrors({ organization: orgConflictMessage });
          setCurrentStep(2);
        } else {
          setErrors({ email: orgConflictMessage });
          setCurrentStep(1);
        }
        setSubmitting(false);
        return;
      }
      if (
        lower.includes('email') &&
        (lower.includes('already') || lower.includes('registered'))
      ) {
        setErrors({
          email: isPlatformAdmin
            ? 'A user with this email already exists. Choose Maintainer or Developer to add them to another organization, or use Edit User to manage existing access.'
            : 'A user with this email already belongs to this organization. Use Edit User to update their details.',
        });
        setCurrentStep(1);
      } else if (isAdminOrgInviteError(message)) {
        setErrors({
          email: ADMIN_ACCOUNT_EMAIL_ERROR,
        });
        setCurrentStep(1);
        setSubmitting(false);
        return;
      }
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  const selectedRole = availableRoles.find(
    (r) => r.value === formData.platformRole
  );

  return (
    <>
      <OnboardingSubHeader title="User Creation" showBackButton />

      <OnboardingStepper steps={USER_STEPS} currentStep={currentStep} />

      <main className="bg-surface-muted px-8 py-8">
        <div className="max-w-3xl mx-auto">
          {currentStep === 1 && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label="Step 1: User Details"
              >
                Step 1: User Details
              </h2>
              <p className="text-gray-600 text-sm mb-8">
                Enter the basic information about the new user.
              </p>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500">
                    Not editable after creation
                  </p>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={formData.email}
                    onChange={(e) => {
                      clearFieldErrors('email', 'organization');
                      invalidateEmailLookup();
                      setFormData({ ...formData, email: e.target.value });
                    }}
                    className={cn(errors.email && 'border-red-500')}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <p className="text-xs text-gray-500">
                      Optional · editable later from Edit User
                    </p>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => {
                        clearFieldErrors('firstName');
                        setFormData({
                          ...formData,
                          firstName: e.target.value,
                        });
                      }}
                      className={cn(errors.firstName && 'border-red-500')}
                    />
                    {errors.firstName && (
                      <p className="text-sm text-red-500">{errors.firstName}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <p className="text-xs text-gray-500">
                      Optional · editable by self or Admin
                    </p>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={(e) => {
                        clearFieldErrors('lastName');
                        setFormData({ ...formData, lastName: e.target.value });
                      }}
                      className={cn(errors.lastName && 'border-red-500')}
                    />
                    {errors.lastName && (
                      <p className="text-sm text-red-500">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <p className="text-xs text-gray-500">
                    Optional - Editable by self or Admin
                  </p>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 555-0100"
                    value={formData.phone}
                    onChange={(e) => {
                      clearFieldErrors('phone');
                      setFormData({ ...formData, phone: e.target.value });
                    }}
                    className={cn(errors.phone && 'border-red-500')}
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-500">{errors.phone}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => navigate(pageRoutes.onboarding)}
                >
                  Cancel
                </Button>
                <Button onClick={handleNext}>Continue</Button>
              </div>
            </OnboardingSurface>
          )}

          {currentStep === 2 && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label="Step 2: Role and Organization"
              >
                Step 2: Role &amp; Organization
              </h2>
              <p className="text-gray-600 text-sm mb-4">
                {isPlatformAdmin
                  ? 'Assign the user\u2019s role and organization.'
                  : 'Add this person to your organization and set their role.'}
              </p>
              <ActiveOrganizationNotice className="mb-4" />

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>
                    Role{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500">
                    {isPlatformAdmin
                      ? 'Admin, or Maintainer / Developer in an organization'
                      : 'Maintainer or Developer in your active organization'}
                  </p>
                  <div className="relative" ref={roleMenu.containerRef}>
                    <button
                      type="button"
                      onClick={roleMenu.toggle}
                      onKeyDown={roleMenu.onKeyDown}
                      aria-haspopup="listbox"
                      aria-expanded={roleMenu.isOpen}
                      aria-controls={roleMenu.menuId}
                      className={cn(
                        'w-full px-3 py-2 border rounded-md text-left flex items-center justify-between bg-white',
                        errors.platformRole
                          ? 'border-red-500'
                          : 'border-gray-200',
                        'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent'
                      )}
                    >
                      <span
                        className={
                          formData.platformRole
                            ? 'text-gray-900'
                            : 'text-gray-500'
                        }
                      >
                        {selectedRole ? selectedRole.label : 'Select role'}
                      </span>
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 text-gray-500 transition-transform',
                          roleMenu.isOpen && 'rotate-180'
                        )}
                      />
                    </button>
                    {roleMenu.isOpen &&
                      roleMenuRect &&
                      createPortal(
                        <div
                          ref={rolePortalMenuRef}
                          id={roleMenu.menuId}
                          role="listbox"
                          className="border border-gray-200 bg-white"
                          style={{
                            position: 'fixed',
                            top: roleMenuRect.top,
                            left: roleMenuRect.left,
                            width: roleMenuRect.width,
                            maxHeight: roleMenuRect.maxHeight,
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            zIndex: 2147483646,
                            borderRadius: 6,
                            boxShadow:
                              '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                          }}
                        >
                          {availableRoles.map((role, index) => (
                            <button
                              key={role.value}
                              type="button"
                              role="option"
                              id={roleMenu.getOptionId(index)}
                              aria-selected={formData.platformRole === role.value}
                              onClick={() => roleMenu.select(index)}
                              onMouseEnter={() => roleMenu.setActiveIndex(index)}
                              className={cn(
                                'w-full px-3 py-2.5 text-left text-sm',
                                formData.platformRole === role.value &&
                                  'bg-accent/5',
                                roleMenu.activeIndex === index
                                  ? 'bg-gray-100'
                                  : 'hover:bg-gray-50'
                              )}
                            >
                              <RoleMenuOption
                                role={role}
                                selected={formData.platformRole === role.value}
                              />
                            </button>
                          ))}
                        </div>,
                        document.body
                      )}
                  </div>
                  {errors.platformRole && (
                    <p className="text-sm text-red-500">{errors.platformRole}</p>
                  )}
                </div>

                {isPlatformAdmin && selectedOrgName && !creatingPlatformAdmin ? (
                  <PlatformAdminOrgNotice
                    organizationName={selectedOrgName}
                    entityLabel="user"
                  />
                ) : null}

                <div className="space-y-2">
                  {creatingPlatformAdmin ? (
                    <>
                      <Label>
                        Organization
                        {orgRequiredForRole && (
                          <span className="text-red-500"> *</span>
                        )}
                      </Label>
                      <p className="text-xs text-gray-500">
                        Not required for Admin. Organization membership applies
                        only to Maintainer or Developer roles.
                      </p>
                      <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                        Admins have global access and are not tied to a client
                        organization.
                      </p>
                    </>
                  ) : isPlatformAdmin ? (
                    <>
                      <Label>
                        Organization
                        {orgRequiredForRole && (
                          <span className="text-red-500"> *</span>
                        )}
                      </Label>
                      <p className="text-xs text-gray-500">
                        Search and select the client organization for this user.
                      </p>
                  <div className="relative" ref={orgMenu.containerRef}>
                    <button
                      type="button"
                      onClick={orgMenu.toggle}
                      onKeyDown={orgMenu.onKeyDown}
                      aria-haspopup="listbox"
                      aria-expanded={orgMenu.isOpen}
                      aria-controls={orgMenu.menuId}
                      className={cn(
                        'w-full px-3 py-2 border rounded-md text-left flex items-center justify-between gap-2 bg-white',
                        errors.organization
                          ? 'border-red-500'
                          : 'border-gray-200',
                        'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent'
                      )}
                    >
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate',
                          formData.organization
                            ? 'text-gray-900'
                            : 'text-gray-500'
                        )}
                        title={formData.organization || undefined}
                      >
                        {formData.organization
                          ? formData.organization
                          : 'Search and select organization'}
                      </span>
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 text-gray-500 transition-transform',
                          orgMenu.isOpen && 'rotate-180'
                        )}
                      />
                    </button>
                    {orgMenu.isOpen &&
                      orgMenuRect &&
                      createPortal(
                        <div
                          ref={orgPortalMenuRef}
                          className="border border-gray-200 bg-white shadow-lg"
                          style={{
                            position: 'fixed',
                            top: orgMenuRect.top,
                            left: orgMenuRect.left,
                            width: orgMenuRect.width,
                            maxHeight: orgMenuRect.maxHeight,
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            zIndex: 2147483646,
                            borderRadius: 6,
                            boxShadow:
                              '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                          }}
                        >
                          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                            <div className="relative">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                ref={orgSearchInputRef}
                                type="text"
                                placeholder="Search organizations..."
                                value={orgSearchQuery}
                                onChange={(e) =>
                                  setOrgSearchQuery(e.target.value)
                                }
                                onKeyDown={orgMenu.onKeyDown}
                                role="combobox"
                                aria-expanded={orgMenu.isOpen}
                                aria-controls={orgMenu.menuId}
                                aria-autocomplete="list"
                                aria-activedescendant={orgMenu.activeOptionId}
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-accent"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div
                            id={orgMenu.menuId}
                            role="listbox"
                          >
                            {filteredOrganizations.length > 0 ? (
                              filteredOrganizations.map((org, index) => (
                                <button
                                  key={org.id}
                                  type="button"
                                  role="option"
                                  id={orgMenu.getOptionId(index)}
                                  aria-selected={
                                    formData.organization === org.name
                                  }
                                  onClick={() => orgMenu.select(index)}
                                  onMouseEnter={() =>
                                    orgMenu.setActiveIndex(index)
                                  }
                                  className={cn(
                                    'w-full px-3 py-2 text-left text-sm flex items-center justify-between',
                                    formData.organization === org.name &&
                                      'bg-accent/5',
                                    orgMenu.activeIndex === index
                                      ? 'bg-gray-100'
                                      : 'hover:bg-gray-50'
                                  )}
                                >
                                  <span className="min-w-0 flex-1 break-words text-left">
                                    {org.name}
                                  </span>
                                  {formData.organization === org.name && (
                                    <Check className="w-4 h-4 text-accent" />
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No organizations found
                              </div>
                            )}
                          </div>
                          {isPlatformAdmin && (
                            <div className="border-t border-gray-100 sticky bottom-0 bg-white">
                              <button
                                type="button"
                                onClick={() => {
                                  orgMenu.close();
                                  setShowAddOrgModal(true);
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-accent hover:bg-gray-50"
                              >
                                <Plus className="h-4 w-4" />
                                Add New Organization
                              </button>
                            </div>
                          )}
                        </div>,
                        document.body
                      )}
                  </div>
                    </>
                  ) : (
                    <LockedField
                      label="Organization"
                      required={orgRequiredForRole}
                      hint="Users are invited to your active organization."
                      value={
                        formData.organization ||
                        currentUser?.organization ||
                        'N/A'
                      }
                    />
                  )}
                  {errors.organization && (
                    <p className="text-sm text-red-500">{errors.organization}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
                <Button onClick={handleNext}>Continue</Button>
              </div>
            </OnboardingSurface>
          )}

          {currentStep === 3 && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label="Step 3: Project Assignments"
              >
                Step 3: Project Assignments
              </h2>

              {creatingPlatformAdmin ? (
                <>
                  <p className="text-gray-600 text-sm mb-6">
                    Review how project access works for the role you selected.
                  </p>
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <p className="font-medium">No project assignments for Admin</p>
                    <p className="mt-1 text-amber-800">
                      Admins have access to all projects. Individual
                      project assignments are not required. Continue to review
                      and create the account.
                    </p>
                  </div>
                </>
              ) : creatingMaintainer ? (
                <>
                  <p className="text-gray-600 text-sm mb-6">
                    Review how project access works for the role you selected.
                  </p>
                  <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                    <p className="font-medium">No project assignments for Maintainer</p>
                    <p className="mt-1 text-sky-800">
                      Maintainers can view and manage all projects in{' '}
                      <strong>{selectedOrgName || 'their organization'}</strong>.
                      Individual project assignments are not required. Continue
                      to review and create the account.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-600 text-sm mb-2">
                    Assign this user to projects within{' '}
                    <span className="font-medium text-gray-900">
                      {selectedOrgName || 'their organization'}
                    </span>
                    .
                  </p>
                  <p className="text-xs text-gray-500 mb-8">
                    Developers only see projects they are assigned to. This step
                    is optional; you can assign projects later from Edit User.
                  </p>

                  {(orgProjectsLoading || orgProjectsHasMore) &&
                    orgProjectsTotalCount > 0 && (
                      <p className="mb-3 text-xs text-gray-500">
                        {orgProjectsLoading && orgProjects.length === 0
                          ? 'Loading projects…'
                          : `Showing ${orgProjects.length} of ${orgProjectsTotalCount} projects`}
                      </p>
                    )}

                  {orgProjectsLoading && orgProjects.length === 0 ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner size="lg" className="text-brand" />
                    </div>
                  ) : (
                  <OnboardingNestedPanel
                    ref={orgProjectsScrollRef}
                    className="max-h-80 divide-y divide-gray-200 overflow-y-auto"
                  >
                    {assignableProjects.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-500">
                        No projects exist in{' '}
                        <span className="font-medium text-gray-700">
                          {selectedOrgName || 'this organization'}
                        </span>{' '}
                        yet. You can create the user now and assign projects
                        later, or onboard a project first via Project Onboarding.
                      </div>
                    ) : (
                      assignableProjects.map((project) => {
                        const isSelected = selectedProjects.includes(project.id);
                        return (
                          <button
                            key={project.id}
                            type="button"
                            onClick={() => toggleProjectAssignment(project.id)}
                            className={cn(
                              'w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors',
                              isSelected && 'bg-accent/5',
                            )}
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center shrink-0">
                                <Briefcase className="w-5 h-5 text-brand" />
                              </div>
                              <div className="min-w-0 text-left">
                                <p className="font-medium text-gray-900 break-words">
                                  {project.name}
                                </p>
                                {isPlatformAdmin && (
                                  <p className="text-sm text-gray-500 break-words">
                                    {project.organization}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div
                              className={cn(
                                'w-5 h-5 rounded border-2 flex items-center justify-center',
                                isSelected
                                  ? 'bg-accent border-accent'
                                  : 'border-gray-300',
                              )}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                    <div
                      ref={orgProjectsSentinelRef}
                      className="flex justify-center px-4 py-3"
                    >
                      {orgProjectsLoadingMore && (
                        <LoadingSpinner size="sm" className="text-brand" />
                      )}
                    </div>
                  </OnboardingNestedPanel>
                  )}

                  {assignableProjects.length > 0 &&
                    selectedProjects.length === 0 && (
                      <div className="mt-6 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                        No projects selected. The user can be assigned to projects
                        later from Edit User.
                      </div>
                    )}
                </>
              )}

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
                <Button onClick={handleNext}>Continue</Button>
              </div>
            </OnboardingSurface>
          )}

          {currentStep === 4 && (
            <OnboardingSurface className="p-8">
              <h2
                className="text-xl font-bold text-brand mb-2 uppercase"
                aria-label="Step 4: Review and Create"
              >
                Step 4: Review &amp; Create
              </h2>
              <p className="text-gray-600 text-sm mb-8">
                Review the user details before creating the account.
              </p>

              <p className="text-xs text-gray-500 mb-6">
                Fields marked with a lock icon cannot be changed after
                creation.
              </p>

              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    User Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <ReviewField
                      icon="pencil"
                      label="First Name"
                      editableHint="(editable)"
                      value={formData.firstName.trim() || 'Not provided'}
                    />
                    <ReviewField
                      icon="pencil"
                      label="Last Name"
                      editableHint="(editable)"
                      value={formData.lastName.trim() || 'Not provided'}
                    />
                    <ReviewField
                      icon="lock"
                      label="Email"
                      value={formData.email}
                    />
                    <ReviewField
                      icon="pencil"
                      label="Phone"
                      editableHint="(editable)"
                      value={formData.phone || 'Not provided'}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    Role &amp; Access
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-500 flex flex-wrap items-center gap-1.5">
                        <Pencil className="w-3 h-3 text-accent shrink-0" />
                        Role
                      </p>
                      <span
                        className={cn(
                          'inline-block px-3 py-1 rounded-full text-sm font-medium mt-1',
                          formData.platformRole === 'admin'
                            ? 'bg-red-100 text-red-700'
                            : formData.platformRole === 'maintainer'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                        )}
                      >
                        {formatPlatformRoleLabel(formData.platformRole)}
                      </span>
                    </div>
                    <ReviewField
                      icon="pencil"
                      label="Organization"
                      value={
                        creatingPlatformAdmin && !formData.organization
                          ? 'Not applicable (Admin)'
                          : formData.organization || 'N/A'
                      }
                    />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    Project Assignments
                  </h3>
                  {creatingPlatformAdmin ? (
                    <p className="text-sm text-gray-600">
                      Not applicable. Admins have global project access.
                    </p>
                  ) : creatingMaintainer ? (
                    <p className="text-sm text-gray-600">
                      Not applicable. Maintainers can access all projects in{' '}
                      <span className="font-medium text-gray-900">
                        {selectedOrgName || 'their organization'}
                      </span>
                      .
                    </p>
                  ) : selectedProjects.length === 0 ? (
                    <p className="text-sm italic text-gray-500">
                      No projects assigned. You can assign projects later from
                      Edit User.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {selectedProjects.map((projectId) => {
                        const project = orgProjects.find(
                          (p) => p.id === projectId,
                        );
                        if (!project) return null;
                        return (
                          <li
                            key={projectId}
                            className="flex items-start gap-3 text-sm min-w-0"
                          >
                            <Briefcase className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                            <span className="font-medium text-gray-900 break-words min-w-0">
                              {project.name}
                            </span>
                            {isPlatformAdmin && (
                              <span className="text-gray-500 break-words min-w-0">
                                ({project.organization})
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 break-words">
                    An invitation email will be sent to{' '}
                    <span className="font-medium break-all">
                      {formData.email}
                    </span>{' '}
                    with instructions to set up their password and access the
                    platform.
                  </p>
                </div>
              </div>

              {submitError && (
                <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-accent hover:bg-accent-hover text-white"
                >
                  {submitting ? (
                    <span className="inline-flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Creating...
                    </span>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </div>
            </OnboardingSurface>
          )}
        </div>
      </main>

      <Dialog
        open={showAddOrgModal}
        onClose={closeAddOrgModal}
        labelledBy="user-add-org-title"
        describedBy="user-add-org-description"
        initialFocusRef={newOrgInputRef}
      >
        <h3
          id="user-add-org-title"
          className="mb-4 text-lg font-bold text-brand"
        >
          Add New Organization
        </h3>
        <p
          id="user-add-org-description"
          className="mb-4 text-sm text-gray-600"
        >
          Enter the name of the new client organization. {ORG_PROJECT_NAME_LENGTH_HINT}.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userNewOrgName">
              Organization Name <span className="text-red-500">*</span>
            </Label>
            <Input
              ref={newOrgInputRef}
              id="userNewOrgName"
              placeholder="Enter organization name"
              maxLength={MAX_ORG_PROJECT_NAME_LENGTH}
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <Button variant="outline" onClick={closeAddOrgModal}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const trimmed = newOrgName.trim();
                if (trimmed) {
                  addOrganizationName(trimmed);
                  clearFieldErrors('organization');
                  setFormData((prev) => ({
                    ...prev,
                    organization: trimmed,
                  }));
                  closeAddOrgModal();
                }
              }}
              disabled={!newOrgName.trim()}
              className="bg-accent text-white hover:bg-accent-hover"
            >
              Add Organization
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

interface ReviewFieldProps {
  icon: 'lock' | 'pencil';
  label: string;
  value: string;
  editableHint?: string;
}

function ReviewField({ icon, label, value, editableHint }: ReviewFieldProps) {
  return (
    // `min-w-0` lets the cell shrink below its intrinsic max-content width
    // inside a `grid-cols-2` track, and `break-all` on the value forces an
    // unbreakable string (e.g. a 60-char email) to wrap rather than spill
    // into the adjacent column.
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-500 flex flex-wrap items-center gap-1.5">
        {icon === 'lock' ? (
          <Lock className="w-3 h-3 shrink-0" />
        ) : (
          <Pencil className="w-3 h-3 text-accent shrink-0" />
        )}
        {label}
        {editableHint && (
          <span className="text-xs text-accent">{editableHint}</span>
        )}
      </p>
      <p className="text-gray-900 mt-1 break-all">{value}</p>
    </div>
  );
}

export default UserOnboarding;
